/*
 * Property data access and presentation helpers shared by listing/detail views.
 * Prefer localized description copy and fall back to available listing text.
 * Geocoding is shared with the agent and office models through ./geocode.
 */
import { property, agent } from '../api';
import { lang, __ } from '../Txlat';
import { getGeoLocation, formatterCurrency } from '../utility';

import { getPropertiesInside } from './property_tile';
export const getPropertiesInside_cosmos = getPropertiesInside;
export const getPropertiesPage = property.getPropertiesPage;
export const getPropertyByID = property.getPropertyByID_cosmos;
export const getSimilarListing = property.getSimilarListing;

// export const getPropertiesInside_cosmos = property.getPropertiesInside_cosmos;
export const getPropertyAgentByID = agent.getAgentByID;

export const getCommercialAgents = agent.getCommercialAgents;

export { getGeoCode, geocodePlace } from './geocode';

export async function getPropertiesNearby_cosmos(keyword, rlp) {
  const { GeoLocation_Longitude, GeoLocation_Latitude } = await getGeoLocation();
  return property.getPropertiesNearBy_cosmos(GeoLocation_Longitude, GeoLocation_Latitude, keyword, rlp);
}

export function getPropertyMetaDescription(property) {
  const { description, description_fr } = property;
  return (lang.get() === 'fr' ? description_fr || description : description || description_fr) || '';
}

export function getPropertyFeatures(property) {
  let features = [];
  if (lang.get() === 'fr') {
    if (property.propFeatures) {
      features = property.propFeatures.map(f => ({ 'key': f.feature[1], 'value': f.values ? f.values[1].join(', ') : '' }));
    }

    if (property.buildingFeatures) {
      for (let i = 0; i < property.buildingFeatures.length; i++) {
        const f = property.buildingFeatures[i];
        features.push({ 'key': f.feature[1], 'value': f.values ? f.values[1].join(', ') : '' });
      }
    }
  } else {
    if (property.propFeatures) {
      features = property.propFeatures.map(f => ({ 'key': f.feature[0], 'value': f.values ? f.values[0].join(', ') : '' }));
    }

    if (property.buildingFeatures) {
      for (let i = 0; i < property.buildingFeatures?.length; i++) {
        const f = property.buildingFeatures[i];
        features.push({ 'key': f.feature[0], 'value': f.values ? f.values[0].join(', ') : '' });
      }
    }
  }

  if (property.neighbourhood) {
    features.push({ 'key': __('Neighbourhood'), 'value': property.neighbourhood });
  }

  if (property.parkingSpaces) {
    features.push({ 'key': __('No. of Parking Spaces'), 'value': property.parkingSpaces });
  }

  if (property.zoning) {
    features.push({ 'key': __('Zoning Type'), 'value': property.zoning });
  }

  if (property.lotSize) {
    features.push({ 'key': __('Lot Size'), 'value': property.lotSize + ' ' + (property.lotUOM ?? '') });
  }

  if (property.buildingWidth && property.buildingDepth && property.buildingUnits) {
    features.push({ 'key': __('Size'), 'value': property.buildingWidth + ' X ' + property.buildingDepth + ' ' + property.buildingUnits });
  }

  if (property.lotFrontage) {
    features.push({ 'key': __('Lot Frontage'), 'value': property.lotFrontage + ' ' + (property.lotDimensionUOM ?? '') });
  }

  if (property.lotDepth) {
    features.push({ 'key': __('Lot Depth'), 'value': property.lotDepth + ' ' + (property.lotDimensionUOM ?? '') });
  }

  if (property.floorSpace) {
    features.push({ 'key': __('Floor Space'), 'value': property.floorSpace + ' ' + property.floorSpaceUnits });
  }

  if (property.totalBaths) {
    features.push({ 'key': __('Bathrooms'), 'value': property.totalBaths });
  }

  if (property.taxAmount) {
    features.push({ 'key': __('Annual Tax Amount'), 'value': formatterCurrency(property.taxAmount) });
  }

  if (property.utilityWater) {
    features.push({ 'key': __('Water'), 'value': property.utilityWater });
  }

  // features?.sort((a, b) => a.key.localeCompare(b.key));
  return features;
}
export function getPropertyDetailStatusPrice(property) {
  const formatter = new Intl.NumberFormat(lang.get() === 'fr' ? 'fr-CA' : 'en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0
  });

  let status = '';
  let price = '';

  switch (property.status) {
    case 'sale':
      status = __('For Sale');
      if (property.price_uom === 'FEETSQ') {
        price = formatter.format(Math.round(property.sale_price)) + __('/Sq.Ft.');
      } else if (property.price_uom === 'ACRE') {
        price = formatter.format(Math.round(property.sale_price)) + __('/Acre');
      } else if (property.price_uom === 'METERSQ') {
        price = formatter.format(Math.round(property.sale_price)) + __('/Sq.m.');
      } else {
        price = formatter.format(Math.round(property.sale_price));
      }
      break;
    case 'lease':
      status = __('For Lease');
      if (!property.leasePrice_uom) {
        price = formatter.format(Math.round(property.lease_price)) + __('/Month');
      } else if (property.leasePrice_uom === 'FEETSQ') {
        price = formatter.format(Math.round(property.lease_price)) + __('/Sq.Ft.');
      } else if (property.leasePrice_uom === 'ACRE') {
        price = formatter.format(Math.round(property.lease_price)) + __('/Acre');
      } else if (property.leasePrice_uom === 'METERSQ') {
        price = formatter.format(Math.round(property.lease_price)) + __('/Sq.m.');
      } else {
        price = formatter.format(Math.round(property.lease_price)) + __('/Month');
      }
      break;

    case 'both':
      status = __('For Sale') + ' / ' + __('For Lease');
      price = formatter.format(Math.round(property.sale_price)) + '   ' + formatter.format(Math.round(property.lease_price)) + __('/Month');
      break;
  }
  return { status, price };
}

// Feed update dates use yy-MM-dd:HH:mm:ss; interpret unzoned timestamps as UTC.
export const propertyDate = value => {
  let raw = String(value || '').trim();
  if (/^\d{2}-\d{2}-\d{2}:\d{2}:\d{2}:\d{2}$/.test(raw)) {
    raw = '20' + raw.slice(0, 8) + 'T' + raw.slice(9);
  }
  raw = raw.replace(/\//g, '-').replace(' ', 'T');
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw) && !/(Z|[+-]\d{2}:?\d{2})$/.test(raw)) { raw += 'Z'; }
  const epoch = Date.parse(raw);
  return Number.isFinite(epoch) ? epoch : 0;
};
const compareMls = (left, right) => {
  const a = String(left.mls_id || left.id || ''); const b = String(right.mls_id || right.id || '');
  return a < b ? -1 : a > b ? 1 : (left.jump_id || 0) - (right.jump_id || 0);
};
export const sortProperties = function (properties, sortBy) {
  switch (sortBy) {
    case 'a_price': {
      properties.sort((p1, p2) => {
        if (p1.sale_price === p2.sale_price) {
          return p1.lease_price - p2.lease_price || compareMls(p1, p2);
        } else {
          return p1.sale_price - p2.sale_price || compareMls(p1, p2);
        }
      });
      break;
    }
    case 'd_price': {
      properties.sort((p1, p2) => {
        if (p1.sale_price === p2.sale_price) {
          return p2.lease_price - p1.lease_price || compareMls(p1, p2);
        } else {
          return p2.sale_price - p1.sale_price || compareMls(p1, p2);
        }
      });
      break;
    }
    case 'a_date': {
      properties.sort((p1, p2) => {
        return propertyDate(p1.last_updated) - propertyDate(p2.last_updated) || compareMls(p1, p2);
      });
      break;
    }

    case 'd_date': {
      properties.sort((p1, p2) => {
        return propertyDate(p2.last_updated) - propertyDate(p1.last_updated) || compareMls(p1, p2);
      });
      break;
    }

    default: {
      properties.sort((p1, p2) => {
        return p1.distance - p2.distance || compareMls(p1, p2);
      });
    }
  }
  return properties;
};
